/**
 * Mobile App Store Listing
 *
 * The copy App Store Connect and Google Play show a shopper, one record per app,
 * store and locale, stored in the `MobileAppStoreListing` table. Shape per the
 * store-listing capture plan (docs/plans/2026-09-02-0337-feat-store-listing-capture-plan.md
 * §3, §5), plus `contactEmail`, which Google Play requires on a listing (school app
 * design plan, KTD9).
 *
 * THE APP'S WORKING COPY, NOT WHAT THE STORE SHOWS. Saving sends nothing to either
 * store. A staff release on a release line pushes the text to App Store Connect or
 * Google Play and freezes what it sent into the app × line record's shipped
 * snapshot (release lines U13), so "what the store has" is that snapshot, never this
 * row. Any surface that edits it must say when the change will reach the store.
 *
 * THE CAPS ARE THE VENDORS', AND THEY ARE ENFORCED HERE rather than only in a form:
 * a form is one write path of several, which is the lesson `displayName` taught
 * (PR #2883 had to cap all three). A value over a cap is not a partial listing, it
 * is one the store will refuse.
 *
 * NULL MEANS NEVER SET. Every field is nullable and nothing coerces an empty string
 * to null or the other way round, so "never filled in" and "deliberately cleared to
 * blank" stay distinguishable on the way back out. Completeness counts both as
 * unfilled.
 *
 * Names avoid `MobileAppStoreListing*`: that prefix already belongs to the per-store
 * listing STATE block in `mobile-deployment.ts`, and both reach the root barrel.
 */

import { z } from 'zod'

/** The two stores a listing is kept for. Strings in the database, closed here. */
export const STORE_LISTING_STORES = ['apple', 'play'] as const
export const StoreListingStoreSchema = z.enum(STORE_LISTING_STORES)
export type StoreListingStore = z.infer<typeof StoreListingStoreSchema>

/**
 * The locales the API reads and writes. The table is keyed for more (Canada is
 * already in `regionMatrix`); nothing offers more yet, so adding one is a change
 * here, not a schema promotion.
 */
export const STORE_LISTING_LOCALES = ['en-US'] as const
export const StoreListingLocaleSchema = z.enum(STORE_LISTING_LOCALES)
export type StoreListingLocale = z.infer<typeof StoreListingLocaleSchema>

/** The only locale any school or super-admin surface reads and writes today. */
export const DEFAULT_STORE_LISTING_LOCALE: StoreListingLocale = 'en-US'

/**
 * The vendors' character limits. Exported for the editors' counters, so a counter
 * and the server can never disagree about where the limit is.
 *
 * `title` is 30 at both stores. `subtitle`, `keywords` and `promotionalText` are
 * Apple's; `shortDescription` is Play's. `whatsNew` is 4000 at both.
 */
export const STORE_LISTING_FIELD_CAPS = {
  title: 30,
  subtitle: 30,
  shortDescription: 80,
  description: 4000,
  keywords: 100,
  promotionalText: 170,
  whatsNew: 4000,
  // Apple's App Review Information → Notes. Play publishes no cap for App access instructions;
  // it takes the same ceiling so one editor counter serves both.
  appleReviewNotes: 4000,
  playAppAccessInstructions: 4000
} as const

/**
 * Ceilings for the fields no vendor caps: long enough for any real link or
 * address, short enough that a pasted document is refused.
 */
const URL_MAX = 2048
const EMAIL_MAX = 254
const CATEGORY_MAX = 64

const cappedText = (max: number) => z.string().max(max).nullable()

/**
 * https only, following `footerLinkUrl` in `mobile-app-onboarding.ts`. A store
 * rejects anything else, and a `javascript:` link has no business in a record a
 * person will copy from.
 */
const httpsUrl = z.string().max(URL_MAX).url().startsWith('https://').nullable()

/**
 * Every column of a listing, as the super-admin read returns it. Store-specific
 * fields are carried on both stores' rows and are null on the store they do not
 * belong to; the write contracts below are what keep them there.
 */
export const StoreListingRecordSchema = z.object({
  title: cappedText(STORE_LISTING_FIELD_CAPS.title),
  subtitle: cappedText(STORE_LISTING_FIELD_CAPS.subtitle),
  shortDescription: cappedText(STORE_LISTING_FIELD_CAPS.shortDescription),
  description: cappedText(STORE_LISTING_FIELD_CAPS.description),
  keywords: cappedText(STORE_LISTING_FIELD_CAPS.keywords),
  promotionalText: cappedText(STORE_LISTING_FIELD_CAPS.promotionalText),
  whatsNew: cappedText(STORE_LISTING_FIELD_CAPS.whatsNew),
  marketingUrl: httpsUrl,
  supportUrl: httpsUrl,
  privacyPolicyUrl: httpsUrl,
  contactEmail: z.string().max(EMAIL_MAX).email().nullable(),
  primaryCategory: cappedText(CATEGORY_MAX),
  secondaryCategory: cappedText(CATEGORY_MAX),
  appleReviewNotes: cappedText(STORE_LISTING_FIELD_CAPS.appleReviewNotes),
  playAppAccessInstructions: cappedText(STORE_LISTING_FIELD_CAPS.playAppAccessInstructions)
})
export type StoreListingRecord = z.infer<typeof StoreListingRecordSchema>

export const StoreListingFieldSchema = StoreListingRecordSchema.keyof()
export type StoreListingField = z.infer<typeof StoreListingFieldSchema>

/**
 * The fields a SCHOOL admin may write, per store (school app design plan KTD11;
 * release lines U8, R10).
 *
 * The school form is store-neutral: `title`, `description`, `marketingUrl`, the
 * contact email and the two other links go to both rows, `subtitle`, `keywords`
 * and `promotionalText` to Apple, `shortDescription` to Play. `whatsNew` and the
 * categories are stored but not offered to schools; School Journey owns them.
 *
 * Every value a school sends passes the same field rules as the super-admin
 * lane's, because both request contracts are built from
 * `StoreListingRecordSchema` and the service re-checks every patch with
 * {@link StoreListingPatchSchema}, whichever lane sent it.
 */
export const SCHOOL_STORE_LISTING_FIELDS = {
  apple: [
    'title',
    'subtitle',
    'description',
    'keywords',
    'promotionalText',
    'contactEmail',
    'supportUrl',
    'marketingUrl',
    'privacyPolicyUrl'
  ],
  play: ['title', 'shortDescription', 'description', 'contactEmail', 'supportUrl', 'marketingUrl', 'privacyPolicyUrl']
} as const satisfies Record<StoreListingStore, readonly StoreListingField[]>

/**
 * The fields readiness counts as a store's listing, which is why the counts read
 * "7" for Apple and "6" for Play. Narrower than what a school may write: the
 * promotional text and the marketing link are optional at both stores, so a
 * listing without them is still one a person could submit.
 */
export const STORE_LISTING_READINESS_FIELDS = {
  apple: ['title', 'subtitle', 'description', 'keywords', 'contactEmail', 'supportUrl', 'privacyPolicyUrl'],
  play: ['title', 'shortDescription', 'description', 'contactEmail', 'supportUrl', 'privacyPolicyUrl']
} as const satisfies Record<StoreListingStore, readonly StoreListingField[]>

/**
 * A partial listing write, checked field by field with the vendors' caps and the
 * https rule. The ONE value check every lane runs (release lines U8): the school
 * and super-admin request contracts are picks of the same record schema, and the
 * listing service parses every patch with this before it writes, so a caller that
 * reached the service without a request contract (an internal script, a future
 * lane) is held to the same rules.
 */
export const StoreListingPatchSchema = StoreListingRecordSchema.partial()
export type StoreListingPatch = z.infer<typeof StoreListingPatchSchema>

/**
 * The fields a SUPER-ADMIN may write, per store: every column that store actually has. Apple has
 * no short description; Play has no subtitle, keywords or promotional text. `marketingUrl` is the
 * Play listing's contact website.
 */
export const ADMIN_STORE_LISTING_FIELDS = {
  apple: [
    'title',
    'subtitle',
    'description',
    'keywords',
    'promotionalText',
    'whatsNew',
    'supportUrl',
    'marketingUrl',
    'privacyPolicyUrl',
    'contactEmail',
    'primaryCategory',
    'secondaryCategory',
    // Super-admin only, and absent from `SCHOOL_STORE_LISTING_FIELDS` on purpose: this is how a
    // reviewer signs in, which is ours to write and not a school's.
    'appleReviewNotes'
  ],
  play: [
    'title',
    'shortDescription',
    'description',
    'whatsNew',
    // Readiness counts a Play support URL and the school lane may write one, so the super-admin lane
    // must be able to as well — without it a Play listing filled from this lane could never read as
    // complete (SCH-2236).
    'supportUrl',
    'marketingUrl',
    'privacyPolicyUrl',
    'contactEmail',
    'primaryCategory',
    'playAppAccessInstructions'
  ]
} as const satisfies Record<StoreListingStore, readonly StoreListingField[]>

/**
 * What `app.store_listing_updated` carries (KTD14).
 *
 * `source` names the lane the write came through, by route. It is a field on this
 * payload rather than a reuse of `ONBOARDING_CONFIG_SOURCES`: that union describes
 * a different table's history rows, and the only listing writer today is the
 * school route. A super-admin writing through that route is recorded under the
 * same source, with their own actor on the event row.
 *
 * Only field NAMES are recorded, never values, as `app.branding_config_updated`
 * does: the values are on the row, and a 4000-character description has no
 * business in an append-only table.
 */
export const STORE_LISTING_WRITE_SOURCES = ['school_admin', 'super_admin'] as const
export const StoreListingUpdatedEventPayloadSchema = z.object({
  store: StoreListingStoreSchema,
  locale: StoreListingLocaleSchema,
  changedFields: z.array(StoreListingFieldSchema).min(1),
  source: z.enum(STORE_LISTING_WRITE_SOURCES)
})
export type StoreListingUpdatedEventPayload = z.infer<typeof StoreListingUpdatedEventPayloadSchema>
